import React from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'dripsy';
import { View } from 'react-native';
import { observer } from 'mobx-react-lite';

import {
  BaseText,
  GooglePaySvg,
  ApplePaySvg,
  PayPalSvg,
  Separator,
  LinkSvg,
} from '@components';
import { PaymentMethod } from '@models/Viewer/PaymentMethodModel';
import {
  PaymentMethodTypeEnum,
  PaymentMethodWalletEnum,
} from '@constants';

const PropRow = styled(View)({
  width: '100%',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const PropTitle = styled(BaseText)({
  color: '$text400',
  variant: 'text.medium',
});

const PropValue = styled(BaseText)({
  color: '$heading800',
  variant: 'text.medium',
});

function renderPaymentMethodWalletForCard(
  paymentMethod: PaymentMethod,
) {
  switch (paymentMethod.wallet) {
    case PaymentMethodWalletEnum.ApplePay:
      return (
        <ApplePaySvg
          contentColor="$heading900"
          viewBox="0 0 55 22"
          height={18}
          width={41}
        />
      );
    case PaymentMethodWalletEnum.GooglePay:
      return (
        <GooglePaySvg
          contentColor="$heading900"
          viewBox="0 0 62 25"
          height={18}
          width={47}
        />
      );
    default:
      return <PropValue>-</PropValue>;
  }
}

function renderPaymentMethodWallet(paymentMethod: PaymentMethod) {
  switch (paymentMethod.type) {
    case PaymentMethodTypeEnum.PayPal:
      return <PayPalSvg height={18} width={60} />;
    case PaymentMethodTypeEnum.Link:
      return <LinkSvg height={18} width={54} />;
    default:
      return renderPaymentMethodWalletForCard(paymentMethod);
  }
}

interface IPaymentMethodItemProps {
  item: PaymentMethod;
}

export const PaymentMethodItemProps =
  observer<IPaymentMethodItemProps>(({ item }) => {
    const { t } = useTranslation();

    switch (item.type) {
      case PaymentMethodTypeEnum.PayPal:
        return (
          <View>
            <PropRow>
              <PropTitle>{t('creditCards.wallet')}</PropTitle>
              {renderPaymentMethodWallet(item)}
            </PropRow>

            <Separator />

            <PropRow>
              <PropTitle>{t('creditCards.email')}</PropTitle>
              <PropValue>{item.email}</PropValue>
            </PropRow>

            <Separator />

            <PropRow>
              <PropTitle>{t('creditCards.payerId')}</PropTitle>
              <PropValue>{item.payerId ?? '-'}</PropValue>
            </PropRow>

            <Separator />

            <PropRow>
              <PropTitle>{t('creditCards.country')}</PropTitle>
              <PropValue>{item.country ?? '-'}</PropValue>
            </PropRow>
          </View>
        );

      case PaymentMethodTypeEnum.Link:
        return (
          <View>
            <PropRow>
              <PropTitle>{t('creditCards.wallet')}</PropTitle>
              {renderPaymentMethodWallet(item)}
            </PropRow>

            <Separator />

            <PropRow>
              <PropTitle>{t('creditCards.email')}</PropTitle>
              <PropValue>{item.email}</PropValue>
            </PropRow>
          </View>
        );

      default:
        return (
          <View>
            <PropRow>
              <PropTitle>{t('creditCards.wallet')}</PropTitle>
              {renderPaymentMethodWallet(item)}
            </PropRow>

            <Separator />

            <PropRow>
              <PropTitle>{t('creditCards.cardNumber')}</PropTitle>
              <PropValue>{item.formattedNumber}</PropValue>
            </PropRow>

            <Separator />

            <PropRow>
              <PropTitle>{t('creditCards.expiry')}</PropTitle>
              <PropValue>{item.exp}</PropValue>
            </PropRow>

            <Separator />

            <PropRow>
              <PropTitle>{t('creditCards.cardBrand')}</PropTitle>
              <PropValue>{item.formattedBrand}</PropValue>
            </PropRow>
          </View>
        );
    }
  });
